import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Gavel, RotateCcw } from "lucide-react";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { API, money } from "./shared";

const box = { marginTop: 14, padding: 14, border: "1px solid var(--line)", borderRadius: 8, background: "#F8FAFC" };

export function useRevisionSummary(briefId, deps = []) {
  const [summary, setSummary] = useState(null);
  const load = () => axios.get(`${API}/design/briefs/${briefId}/revision-summary`).then(r => setSummary(r.data)).catch(() => setSummary(null));
  useEffect(() => { load(); }, [briefId, ...deps]); // eslint-disable-line
  return [summary, load];
}

export function RevisionTracker({ summary }) {
  if (!summary) return null;
  const { free_revisions: free, revisions_used: used, revision_rate_inr: rate, paid_revisions_used: paidUsed, revision_fees_paid_inr: fees } = summary;
  return (
    <div data-testid="revision-tracker" style={{ ...box, marginTop: 10, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <b style={{ font: "600 13px Outfit" }}><RotateCcw size={13} style={{ verticalAlign: -2 }} /> Revisions</b>
        <span data-testid="revision-tracker-count" style={{ fontSize: 12, color: "#334155" }}>
          {Math.min(used, free)} of {free} free used{paidUsed ? ` · ${paidUsed} paid` : ""}
        </span>
      </div>
      <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
        {Array.from({ length: free }).map((_, i) => (
          <div key={i} data-testid={`revision-slot-${i + 1}`} style={{ flex: 1, height: 6, borderRadius: 3, background: i < used ? "#1d4ed8" : "#E2E8F0" }} />
        ))}
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 12, color: "#64748b" }} data-testid="revision-tracker-rate">
        After {free} free revisions, each extra round costs <b>{rate ? money(rate) : "—"}</b> (paid upfront into escrow).
        {fees ? <> Paid so far: <b>{money(fees)}</b>.</> : null}
      </p>
    </div>
  );
}

export async function payForRevision(brief, user) {
  const [cfg, order] = await Promise.all([
    axios.get(`${API}/razorpay/config`).then(r => r.data),
    axios.post(`${API}/design/briefs/${brief.id}/paid-revision/order`).then(r => r.data),
  ]);
  const resp = await openRazorpayCheckout({
    keyId: cfg.key_id,
    order,
    name: "GoPrintPack · Paid revision",
    description: `Extra revision · ${brief.title || brief.id}`,
    prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
    notes: { brief_id: brief.id, flow: "paid_revision" },
  });
  await axios.post(`${API}/razorpay/verify`, resp);
}

const REASONS = ["Quality not as briefed", "Missed deadline", "Agency unresponsive", "Other"];

export function DesignDisputePanel({ brief, role, userId, onChange }) {
  const [disputes, setDisputes] = useState([]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => axios.get(`${API}/design/briefs/${brief.id}/disputes`).then(r => setDisputes(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, [brief.id, brief.dispute_status]); // eslint-disable-line
  const isBuyer = role === "buyer" && brief.buyer_id === userId;
  const canRaise = isBuyer && brief.status === "in_progress" && brief.dispute_status !== "open";
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/design/briefs/${brief.id}/disputes`, { reason, description: desc });
      toast.success("Dispute raised", { description: "Escrow is frozen. Our team will review within 48 hours." });
      setOpen(false); setDesc(""); await load(); onChange && onChange();
    } catch (e) { setErr(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not raise dispute"); }
    finally { setBusy(false); }
  };
  const latest = disputes[0];
  return (
    <div data-testid="design-dispute-panel">
      {latest && (
        <div data-testid="design-dispute-status" style={{ ...box, background: latest.status === "open" ? "#FFFBEB" : "#F0FDF4", borderColor: latest.status === "open" ? "#FCD34D" : "#86EFAC" }}>
          <b style={{ font: "600 13px Outfit" }}><Gavel size={13} style={{ verticalAlign: -2 }} /> Dispute · {latest.status === "open" ? "Under admin review" : "Resolved"}</b>
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "#475569" }}>{latest.reason} — {latest.description}</p>
          {latest.resolution && (
            <p style={{ margin: "6px 0 0", fontSize: 12 }} data-testid="design-dispute-resolution">
              Verdict: <b>{latest.resolution.verdict.replace(/_/g, " ")}</b> · Refund to buyer {money(latest.resolution.refund_inr)} · Paid to agency {money(latest.resolution.agency_payout_inr)}
              {latest.resolution.notes ? <><br />Admin note: {latest.resolution.notes}</> : null}
            </p>
          )}
        </div>
      )}
      {canRaise && !open && (
        <button data-testid="design-dispute-open-btn" className="secondary-btn" style={{ marginTop: 12 }} onClick={() => setOpen(true)}>
          <AlertTriangle size={14} /> Not satisfied? Raise a dispute
        </button>
      )}
      {open && (
        <div style={box} data-testid="design-dispute-form">
          <b style={{ font: "600 13px Outfit" }}>Raise a dispute</b>
          <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 8px" }}>Escrow (project fee + paid revisions) is frozen while an admin reviews. The admin may order a full or partial refund.</p>
          <select data-testid="design-dispute-reason" value={reason} onChange={e => setReason(e.target.value)} style={{ width: "100%", padding: 8, borderRadius: 5, border: "1px solid var(--line)", font: "13px Outfit" }}>
            {REASONS.map(r => <option key={r}>{r}</option>)}
          </select>
          <textarea data-testid="design-dispute-desc" rows={3} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Explain what went wrong (min 10 characters)"
            style={{ width: "100%", font: "13px Outfit", padding: 10, border: "1px solid var(--line)", borderRadius: 5, marginTop: 8 }} />
          {err && <div style={{ color: "#b91c1c", fontSize: 12 }} data-testid="design-dispute-err">{String(err)}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button className="secondary-btn" data-testid="design-dispute-cancel" onClick={() => setOpen(false)}>Cancel</button>
            <button className="primary-btn" data-testid="design-dispute-submit" disabled={busy || desc.trim().length < 10} onClick={submit}>{busy ? "Submitting…" : "Submit dispute"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminDesignDisputes() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => axios.get(`${API}/admin/design-disputes`).then(r => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const resolve = async (d) => {
    const f = form[d.id] || {};
    const verdict = f.verdict || "partial_refund";
    if (!window.confirm(`Resolve dispute on "${d.brief_title}" as ${verdict.replace(/_/g, " ")}? Refunds are issued via Razorpay immediately.`)) return;
    setBusy(d.id);
    try {
      await axios.post(`${API}/admin/design-disputes/${d.id}/resolve`, {
        verdict, refund_pct: verdict === "partial_refund" ? Number(f.pct || 0) : null, notes: f.notes || "",
      });
      const r = await axios.get(`${API}/admin/design-disputes`);
      const done = (r.data || []).find(x => x.id === d.id);
      const failed = (done?.resolution?.refunds || []).filter(x => x.status === "failed").length;
      if (failed) toast.warning(`Dispute resolved, but ${failed} Razorpay refund(s) failed — issue them manually from the Payments tab.`);
      else toast.success("Dispute resolved");
      setRows(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not resolve"); }
    finally { setBusy(null); }
  };
  const set = (id, k, v) => setForm({ ...form, [id]: { ...(form[id] || {}), [k]: v } });
  return (
    <div data-testid="admin-design-disputes" style={{ marginBottom: 24 }}>
      <div className="section-heading"><div><p className="eyebrow">DESIGN PROJECTS</p><h2>Design disputes</h2></div><span>{rows.filter(r => r.status === "open").length} open</span></div>
      {rows.length === 0 ? <div className="pipeline-empty" data-testid="admin-design-disputes-empty">No design disputes.</div> : rows.map(d => {
        const f = form[d.id] || {};
        const verdict = f.verdict || "partial_refund";
        return (
          <div key={d.id} data-testid={`admin-design-dispute-${d.id}`} style={{ ...box, background: "#fff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <b>{d.brief_title || d.brief_id}</b>
              <span className={`status ${d.status === "open" ? "matching" : "completed"}`}>{d.status}</span>
            </div>
            <p style={{ fontSize: 12, color: "#475569", margin: "6px 0" }}>
              Buyer: {d.buyer_name || d.buyer_id} · Agency: {d.agency_name || d.agency_id} · Escrow: <b>{money(d.escrow_total_inr)}</b><br />
              <b>{d.reason}</b> — {d.description}
            </p>
            {d.status === "open" ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 8 }}>
                <select data-testid={`admin-dd-verdict-${d.id}`} value={verdict} onChange={e => set(d.id, "verdict", e.target.value)} style={{ padding: 8, borderRadius: 5, border: "1px solid var(--line)" }}>
                  <option value="full_refund">Full refund to buyer</option>
                  <option value="partial_refund">Partial refund</option>
                  <option value="release_to_agency">Release to agency</option>
                </select>
                <input data-testid={`admin-dd-pct-${d.id}`} type="number" min="1" max="99" placeholder="Refund %" disabled={verdict !== "partial_refund"}
                  value={f.pct || ""} onChange={e => set(d.id, "pct", e.target.value)} style={{ padding: 8, borderRadius: 5, border: "1px solid var(--line)" }} />
                <textarea data-testid={`admin-dd-notes-${d.id}`} rows={2} placeholder="Decision note (shared with both parties)" value={f.notes || ""}
                  onChange={e => set(d.id, "notes", e.target.value)} style={{ gridColumn: "span 2", padding: 8, borderRadius: 5, border: "1px solid var(--line)", font: "13px Outfit" }} />
                {verdict === "partial_refund" && f.pct ? <span style={{ fontSize: 12, gridColumn: "span 2" }}>Buyer gets {money(d.escrow_total_inr * Number(f.pct) / 100)} · Agency gets {money(d.escrow_total_inr * (100 - Number(f.pct)) / 100)}</span> : null}
                <button data-testid={`admin-dd-resolve-${d.id}`} className="primary-btn" style={{ gridColumn: "span 2", justifySelf: "end" }}
                  disabled={busy === d.id || (f.notes || "").trim().length < 5 || (verdict === "partial_refund" && !(Number(f.pct) > 0 && Number(f.pct) < 100))}
                  onClick={() => resolve(d)}>{busy === d.id ? "Resolving…" : "Resolve dispute"}</button>
              </div>
            ) : d.resolution ? (
              <p style={{ fontSize: 12, margin: 0 }}>Verdict: <b>{d.resolution.verdict.replace(/_/g, " ")}</b> · Refund {money(d.resolution.refund_inr)} · Agency {money(d.resolution.agency_payout_inr)} · {d.resolution.notes}
                {(d.resolution.refunds || []).some(x => x.status === "failed") && <span data-testid={`admin-dd-refund-failed-${d.id}`} style={{display:"block",color:"#b91c1c",marginTop:4}}>⚠ Some Razorpay refunds failed — issue manually from Payments.</span>}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
